import { makeAutoObservable, runInAction } from 'mobx';
import { parseCSV, TimeFrame } from 'shared/utils/csvParser';
import sceneStore from './sceneStore';
import {
    SkinnedMesh,
    Object3D,
    Bone,
    Mesh,
    Color,
    MeshStandardMaterial,
} from 'three';
import { frameKeyMap, muscleNormalization } from './frameKeyMap';
import { initialMuscleRotations } from './initialMuscleRotations';

export const csvToMixamoBone: Record<string, string> = {
    позвоночник_наклон_в_стороны: 'mixamorigSpine1',
    позвоночник_наклон_вперед: 'mixamorigSpine1',
    позвоночник_поворот: 'mixamorigSpine1',

    тазобедренный_сустав_л_сгибание: 'mixamorigLeftUpLeg',
    тазобедренный_сустав_л_отведение: 'mixamorigLeftUpLeg',
    коленный_сустав_л_сгибание: 'mixamorigLeftLeg',
    голеностопный_сустав_л_сгибание: 'mixamorigLeftFoot',
    голеностопный_сустав_л_отведение: 'mixamorigRightFoot',

    тазобедренный_сустав_п_сгибание: 'mixamorigRightUpLeg',
    тазобедренный_сустав_п_отведение: 'mixamorigRightUpLeg',
    коленный_сустав_п_сгибание: 'mixamorigRightLeg',
    голеностопный_сустав_п_сгибание: 'mixamorigRightFoot',
    голеностопный_сустав_п_отведение: 'mixamorigRightFoot',

    плечевой_сустав_л_сгибание: 'mixamorigLeftArm',
    плечевой_сустав_л_подъем: 'mixamorigLeftArm',
    локтевой_сустав_л_сгибание: 'mixamorigLeftForeArm',
    лучезапястный_сустав_л_отведение: 'mixamorigLeftHand',
    лучезапястный_сустав_л_сгибание: 'mixamorigLeftHand',

    плечевой_сустав_п_сгибание: 'mixamorigRightArm',
    плечевой_сустав_п_подъем: 'mixamorigRightArm',
    локтевой_сустав_п_сгибание: 'mixamorigRightForeArm',
    лучезапястный_сустав_п_отведение: 'mixamorigRightHand',
    лучезапястный_сустав_п_сгибание: 'mixamorigRightHand',
};

export class MotionStore {
    frames: TimeFrame[] = [];
    currentFrameIndex = 0;

    private currentValues: Record<string, number> = {};
    private targetValues: Record<string, number> = {};
    private lerpSpeed = 5;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
        console.log('[MotionStore] Инициализирован');
    }

    async loadCSV(input: string | Array<Record<string, any>>) {
        try {
            console.log('[MotionStore] loadCSV вызван');
            let text: string;

            if (Array.isArray(input)) {
                console.log('[MotionStore] input - массив', input.length, 'записей');
                const frames: TimeFrame[] = input.map(row => ({
                    time: row.Время || row.time,
                    data: row
                }));
                runInAction(() => {
                    this.frames = frames;
                    this.currentFrameIndex = 0;
                });
                if (this.frames.length) {
                    console.log('[MotionStore] применяем первый кадр');
                    this.applyFrame(this.frames[0]);
                }
                await this.waitForModel();
                return;
            }

            if (typeof input === 'string' && input.startsWith('http')) {
                console.log('[MotionStore] загружаем CSV с URL:', input);
                const response = await fetch(input);
                text = await response.text();
            } else if (typeof input === 'string') {
                console.log('[MotionStore] input - строка CSV');
                text = input;
            } else {
                throw new Error('Неверный формат input для loadCSV');
            }

            const parsedFrames: TimeFrame[] = await parseCSV(text);
            console.log('[MotionStore] CSV распарсен, кадров:', parsedFrames.length);

            runInAction(() => {
                this.frames = parsedFrames;
                this.currentFrameIndex = 0;
            });

            if (this.frames.length) {
                console.log('[MotionStore] применяем первый кадр после parseCSV');
                this.applyFrame(this.frames[0]);
            }

            await this.waitForModel();
            console.log('[MotionStore] модель готова, можно анимировать');
        } catch (err) {
            console.error('[MotionStore] Ошибка загрузки CSV:', err);
        }
    }

    private async waitForModel(timeout = 5000) {
        console.log('[MotionStore] ждем модель...');
        const start = performance.now();
        while (!sceneStore.bodyModel?.modelData) {
            await new Promise((resolve) => setTimeout(resolve, 50));
            if (performance.now() - start > timeout) {
                console.warn('[MotionStore] waitForModel таймаут');
                break;
            }
        }
        console.log('[MotionStore] модель загружена или таймаут');
    }

    private valueToColor(value: number) {
        const clamped = Math.min(Math.max(value, 0), 1);
        const color = new Color();
        const hue = 240 + 120 * clamped;
        color.setHSL(hue / 360, 1, 0.5);
        return color;
    }

    applyFrame(frame: TimeFrame) {
        console.log('[MotionStore] applyFrame, keys:', Object.keys(frame.data).length);
        Object.keys(frame.data).forEach((key) => {
            const value = Number(frame.data[key]);
            if (isNaN(value)) return;
            this.targetValues[key] = value;
            if (!(key in this.currentValues)) this.currentValues[key] = value;
        });
    }

    update(delta: number) {
        const bodyModel = sceneStore.bodyModel;
        if (!bodyModel?.modelData) return;

        Object.keys(this.targetValues).forEach((key) => {
            const mapEntry = frameKeyMap[key];
            if (!mapEntry) return;
            if (mapEntry.type === 'time' || key.toLowerCase() === 'время') return;

            const target = this.targetValues[key];
            const current = this.currentValues[key] ?? target;
            const newValue = current + (target - current) * Math.min(1, this.lerpSpeed * delta);
            this.currentValues[key] = newValue;

            if (mapEntry.type === 'joint' || mapEntry.type === 'muscle') {
                this.applyValueToModel(
                    key,
                    newValue,
                    mapEntry as { type: 'joint' | 'muscle'; axis?: 'x' | 'y' | 'z' }
                );
            }
        });

        bodyModel.modelData?.updateMatrixWorld(true);
        // Лог для отслеживания обновлений
        // console.log('[MotionStore] update, delta:', delta.toFixed(3));
    }

    private applyValueToModel(
        key: string,
        value: number,
        mapEntry: { type: 'joint' | 'muscle'; axis?: 'x' | 'y' | 'z' }
    ) {
        const bodyModel = sceneStore.bodyModel;
        if (!bodyModel) return;

        let targetObjects: (Object3D | Bone)[] = [];

        if (mapEntry.type === 'joint') {
            const boneName = csvToMixamoBone[key];
            if (!boneName) return;
            targetObjects = bodyModel.boneMap[boneName] || [];
        }

        if (mapEntry.type === 'muscle') {
            const muscleMeshes = bodyModel.muscleMap[key];
            if (!muscleMeshes) {
                console.warn(`[MotionStore] Мышца не найдена в muscleMap: "${key}"`);
            } else {
                targetObjects = muscleMeshes;
            }
        }

        if (!targetObjects.length) return;

        const initialConfig = initialMuscleRotations[key];
        const offset = initialConfig?.offset ?? 0;
        const invert = initialConfig?.invert ?? false;
        const finalValue = (invert ? -value : value) + offset;

        targetObjects.forEach((obj) => {
            if (mapEntry.type === 'joint' && obj instanceof Bone) {
                const rad = (finalValue * Math.PI) / 180;
                switch (mapEntry.axis) {
                    case 'x':
                        obj.rotation.x = rad;
                        break;
                    case 'y':
                        obj.rotation.y = rad;
                        break;
                    case 'z':
                        obj.rotation.z = rad;
                        break;
                }
            } else if (
                mapEntry.type === 'muscle' &&
                (obj instanceof Mesh || obj instanceof SkinnedMesh)
            ) {
                const norm = muscleNormalization[key] || 1000;
                const normalizedValue = finalValue / norm;

                if (!(obj.material instanceof MeshStandardMaterial)) {
                    obj.material = new MeshStandardMaterial({ color: 0xffffff });
                }
                const color = this.valueToColor(normalizedValue);
                (obj.material as MeshStandardMaterial).color.copy(color);
                (obj.material as MeshStandardMaterial).roughness = 0.4;
                (obj.material as MeshStandardMaterial).metalness = 0.1;
            }
        });
    }

    nextFrame() {
        if (!this.frames.length) return;
        this.currentFrameIndex = (this.currentFrameIndex + 1) % this.frames.length;
        console.log('[MotionStore] nextFrame', this.currentFrameIndex);
        this.applyFrame(this.frames[this.currentFrameIndex]);
    }

    prevFrame() {
        if (!this.frames.length) return;
        this.currentFrameIndex =
            (this.currentFrameIndex - 1 + this.frames.length) % this.frames.length;
        console.log('[MotionStore] prevFrame', this.currentFrameIndex);
        this.applyFrame(this.frames[this.currentFrameIndex]);
    }
}

export default new MotionStore();
